import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button } from '@ace-clublabs/shared-component-library';
import './EmbedTestParent.scss';

const EmbedTestParent: React.FC = () => {
  const { manifestId } = useParams<{ manifestId: string }>();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [eventLog, setEventLog] = useState('Waiting for events...');
  const [logExpanded, setLogExpanded] = useState(true);
  const pendingStartRef = useRef<object | null>(null);
  const startSentRef = useRef(false);

  const logEvent = useCallback((label: string, data: object) => {
    const timestamp = new Date().toLocaleTimeString();
    const entry = `[${timestamp}] ${label}:\n${JSON.stringify(data, null, 2)}\n\n`;
    setEventLog((prev) => entry + prev);
  }, []);

  const sendStart = useCallback(() => {
    const msg = { source: 'ace-payments-embed-parent', type: 'START', payload: {} };

    if (iframeReady) {
      iframeRef.current?.contentWindow?.postMessage(msg, '*');
      logEvent('SENT TO iframe', msg);
      startSentRef.current = true;
    } else {
      pendingStartRef.current = msg;
    }
  }, [iframeReady, logEvent]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.source !== 'ace-payments-embed') return;

      logEvent('RECEIVED FROM iframe', data);

      switch (data.type) {
        case 'READY':
          setIframeReady(true);
          if (pendingStartRef.current) {
            iframeRef.current?.contentWindow?.postMessage(pendingStartRef.current, event.origin);
            logEvent('SENT TO iframe (queued)', pendingStartRef.current);
            startSentRef.current = true;
            pendingStartRef.current = null;
          }
          break;
        case 'RESIZE':
          if (iframeRef.current && startSentRef.current) {
            iframeRef.current.style.height = `${data.payload.height}px`;
          }
          break;
        case 'PAYMENT_SUCCESS':
          console.log('[EmbedTestParent] PAYMENT_SUCCESS', data.payload);
          break;
        case 'PAYMENT_ERROR':
          console.log('[EmbedTestParent] PAYMENT_ERROR', data.payload);
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [logEvent]);

  return (
    <Box className="embed-test-parent">
      <Box className="embed-test-parent__container">
        <Box className="embed-test-parent__controls">
          <Button id="embed-start" variant="contained" label="Post" onClick={sendStart} />
        </Box>

        <iframe
          ref={iframeRef}
          title="Payment Form"
          className="embed-test-parent__iframe"
          src={`/payments/embed/${manifestId}`}
          scrolling="no"
        />
      </Box>

      <Box
        className={`embed-test-parent__log${logExpanded ? '' : ' embed-test-parent__log--collapsed'}`}
        onClick={!logExpanded ? () => setLogExpanded(true) : undefined}
      >
        <button
          className="embed-test-parent__log-toggle"
          onClick={() => setLogExpanded((v) => !v)}
          aria-label={logExpanded ? 'Collapse log' : 'Expand log'}
          title={logExpanded ? 'Collapse log' : 'Expand log'}
        >
          {logExpanded ? '✕' : '▣'}
        </button>
        {logExpanded && <pre className="embed-test-parent__log-body">{eventLog}</pre>}
      </Box>
    </Box>
  );
};

export default EmbedTestParent;
